// DatabaseService.js - API-based replacement for mock data
// Replace your existing DatabaseService.js with this file
// This connects your React app to the MySQL backend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

class DatabaseService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  // Check if the current session is a demo account (fake token)
  isDemoToken() {
    return this.token && this.token.startsWith('demo_token_');
  }

  // ============================================
  // HTTP HELPER
  // ============================================

  async request(endpoint, options = {}) {
    // Re-read token in case it changed since construction
    this.token = localStorage.getItem('authToken');

    // Demo accounts use fake tokens that the backend will reject.
    // Skip the network call entirely and let callers fall back to mock data.
    if (this.isDemoToken()) {
      throw new Error('Demo mode — API not available');
    }

    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      ...options.headers
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      // Handle 401 - try to refresh token
      if (response.status === 401 && this.refreshToken) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Retry original request with new token
          headers['Authorization'] = `Bearer ${this.token}`;
          const retryResponse = await fetch(url, { ...options, headers });
          if (!retryResponse.ok) {
            const error = await retryResponse.json().catch(() => ({}));
            throw new Error(error.error || 'Request failed');
          }
          return retryResponse.json();
        }
        // Refresh failed, redirect to login
        this.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired');
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `Request failed: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  async tryRefreshToken() {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.refreshToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        this.setToken(data.token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  setToken(token, refreshToken = null) {
    this.token = token;
    localStorage.setItem('authToken', token);
    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem('refreshToken', refreshToken);
    }
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  isAuthenticated() {
    return !!this.token;
  }

  // ============================================
  // AUTHENTICATION
  // ============================================
  
  async login(email, password) {
    const result = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    this.setToken(result.token, result.refreshToken);
    localStorage.setItem('user', JSON.stringify(result.user));
    return result;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }

  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // ============================================
  // DASHBOARD - Role-specific aggregated data
  // ============================================

  async getDashboardData() {
    return this.request('/dashboard');
  }

  // ============================================
  // USERS
  // ============================================

  async getUsers() {
    return this.request('/users');
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(userData)
    });
  }

  // ============================================
  // TENANTS (Banks & Investors)
  // ============================================

  async getTenants(type = null) {
    const query = type ? `?type=${type}` : '';
    return this.request(`/tenants${query}`);
  }

  async getBanks() {
    return this.getTenants('bank');
  }

  async getTenant(id) {
    return this.request(`/tenants/${id}`);
  }

  async createTenant(tenantData) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(tenantData)
    });
  }

  async updateTenant(id, tenantData) {
    return this.request(`/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(tenantData)
    });
  }

  // ============================================
  // ACCOUNTS & BALANCES
  // ============================================

  async getAccounts() {
    return this.request('/accounts');
  }

  async getAccountBalance(accountId) {
    return this.request(`/accounts/${accountId}/balance`);
  }

  // ============================================
  // DEPOSIT CALLS
  // ============================================

  async getDepositCalls(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/deposit-calls${params ? '?' + params : ''}`);
  }

  async getCalls() {
    // Alias for backward compatibility
    return this.getDepositCalls();
  }

  async getDepositCall(id) {
    return this.request(`/deposit-calls/${id}`);
  }

  async getCallById(id) {
    // Alias for backward compatibility
    return this.getDepositCall(id);
  }

  async createDepositCall(callData) {
    return this.request('/deposit-calls', {
      method: 'POST',
      body: JSON.stringify(callData)
    });
  }

  async updateDepositCallStatus(id, status) {
    return this.request(`/deposit-calls/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // ============================================
  // SUBSCRIPTIONS
  // ============================================

  async getSubscriptions() {
    return this.request('/subscriptions');
  }

  async getSubscription(id) {
    return this.request(`/subscriptions/${id}`);
  }

  async createSubscription(subscriptionData) {
    return this.request('/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscriptionData)
    });
  }

  async updateSubscriptionStatus(id, status) {
    return this.request(`/subscriptions/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }

  // ============================================
  // LOANS
  // ============================================

  async getLoans(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/loans${params ? '?' + params : ''}`);
  }

  async getLoan(id) {
    return this.request(`/loans/${id}`);
  }

  async createLoan(loanData) {
    return this.request('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData)
    });
  }

  async approveLoan(id) {
    return this.request(`/loans/${id}/approve`, { method: 'POST' });
  }

  async disburseLoan(id) {
    return this.request(`/loans/${id}/disburse`, { method: 'POST' });
  }

  async processLoanRepayment(id, amount, reference) {
    return this.request(`/loans/${id}/repayment`, {
      method: 'POST',
      body: JSON.stringify({ amount, reference })
    });
  }

  // ============================================
  // INVESTORS
  // ============================================

  async getInvestors() {
    return this.request('/investors');
  }

  async getInvestor(id) {
    return this.request(`/investors/${id}`);
  }

  async createInvestor(investorData) {
    return this.request('/investors', {
      method: 'POST',
      body: JSON.stringify(investorData)
    });
  }

  async updateInvestor(id, investorData) {
    return this.request(`/investors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(investorData)
    });
  }

  async verifyInvestorKYC(id) {
    return this.request(`/investors/${id}/verify-kyc`, { method: 'POST' });
  }

  // ============================================
  // TRANSACTIONS (Read-only - immutable ledger)
  // ============================================

  async getTransactions(filters = {}) {
    const params = new URLSearchParams(filters).toString();
    return this.request(`/transactions${params ? '?' + params : ''}`);
  }

  async getTransaction(id) {
    return this.request(`/transactions/${id}`);
  }

  async getTransactionSummary() {
    return this.request('/transactions/stats/summary');
  }

  // ============================================
  // ANALYTICS HELPERS
  // ============================================

  async getAnalytics(type = 'overview') {
    // This uses the dashboard endpoint and extracts analytics
    const dashboard = await this.getDashboardData();
    return dashboard.metrics || dashboard;
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  formatCurrency(amount, currency = 'KES') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date) {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

// Create and export singleton instance
const db = new DatabaseService();

export { db, DatabaseService };
export default db;
